import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtOdds, fmtMoney, fmtPct } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Wallet, CheckCircle2, XCircle, MinusCircle, Clock, Layers3, ChevronDown, Sparkles, RefreshCw, Target, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
];

export default function MyBets() {
  const [tab, setTab] = useState("all");
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoSettling, setAutoSettling] = useState(false);

  const load = () => {
    setLoading(true);
    const params = tab === "all" ? {} : { status: tab };
    api.get("/clv/bets", { params }).then((r) => {
      // /clv/bets returns { count, bets } with CLV enrichment included
      let rows = r.data.bets;
      if (tab !== "all") rows = rows.filter((b) => b.status === tab);
      setBets(rows);
    }).finally(() => setLoading(false));
  };

  useEffect(load, [tab]);

  const settle = async (id, outcome) => {
    try {
      await api.post(`/bets/${id}/settle`, null, { params: { outcome } });
      toast.success(`Bet marked ${outcome}`);
      load();
    } catch (e) {
      toast.error("Failed to settle bet");
    }
  };

  const autoSettle = async () => {
    setAutoSettling(true);
    try {
      const r = await api.post("/admin/settle-bets");
      const { settled = 0, won = 0, lost = 0, push = 0, final_games = 0 } = r.data || {};
      if (settled > 0) {
        toast.success(`Auto-settled ${settled} bet${settled > 1 ? "s" : ""} · ${won}W · ${lost}L · ${push}P`);
      } else {
        toast.info(final_games ? "No open bets match finished games yet." : "No finished games to settle from.");
      }
      load();
    } catch (e) {
      toast.error("Auto-settle failed");
    } finally {
      setAutoSettling(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="mybets-page">
      <PageHeader
        title="My bets"
        subtitle="Your personal ledger. Log plays, settle outcomes, and watch ROI compound."
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-border bg-gray-0 p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`bets-tab-${t.id}`}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                tab === t.id ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={autoSettle}
          disabled={autoSettling}
          className="iso-btn-outline !py-1.5 !text-xs"
          data-testid="auto-settle-bets-btn"
          title="Pull final scores and settle matching open bets automatically"
        >
          {autoSettling
            ? <><RefreshCw className="size-3.5 animate-spin" /> Checking scores...</>
            : <><Sparkles className="size-3.5" /> Auto-settle from scores</>}
        </button>
      </div>

      {loading && <div className="iso-card p-8 text-center text-sm text-gray-500">Loading bets...</div>}

      {!loading && bets.length === 0 && (
        <div className="iso-card p-12 text-center">
          <Wallet className="mx-auto size-10 text-gray-300" />
          <h3 className="mt-3 font-lexend">No bets here yet</h3>
          <p className="mt-1 text-sm text-gray-500">Run a simulation and click "Track" on any edge to log your first bet.</p>
        </div>
      )}

      <div className="space-y-3">
        {bets.map((b) => b.market === "parlay" ? (
          <ParlayRow key={b.id} bet={b} onSettle={settle} />
        ) : (
          <div key={b.id} className="iso-card flex flex-wrap items-center gap-4 p-4" data-testid={`bet-row-${b.id}`}>
            <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500">
              <Wallet className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900">{b.game_label}</div>
              <div className="mt-0.5 text-xs text-gray-500">
                {b.market} · {b.selection}{b.line != null ? ` (${b.line > 0 ? "+" : ""}${b.line})` : ""} · {fmtOdds(b.odds)}
              </div>
            </div>
            <div className="text-right">
              <div className="iso-label !mb-0">Stake</div>
              <div className="font-lexend text-sm font-bold">{fmtMoney(b.stake)}</div>
            </div>
            <StatusBadge status={b.status} payout={b.payout} />
            <CLVChip clv={b.clv} id={b.id} />
            {b.auto_settled && b.status !== "open" && (
              <span className="iso-chip iso-chip-secondary !text-[10px]" title="Settled automatically from final scores" data-testid={`auto-settled-${b.id}`}>
                <Sparkles className="size-3" /> Auto
              </span>
            )}
            {b.status === "open" && (
              <div className="flex flex-wrap items-center gap-1">
                <button className="iso-btn-ghost !py-1.5 !text-xs !text-green-dark" onClick={() => settle(b.id, "won")} data-testid={`settle-won-${b.id}`}>
                  <CheckCircle2 className="size-3.5" /> Won
                </button>
                <button className="iso-btn-ghost !py-1.5 !text-xs !text-red-dark" onClick={() => settle(b.id, "lost")} data-testid={`settle-lost-${b.id}`}>
                  <XCircle className="size-3.5" /> Lost
                </button>
                <button className="iso-btn-ghost !py-1.5 !text-xs" onClick={() => settle(b.id, "push")} data-testid={`settle-push-${b.id}`}>
                  <MinusCircle className="size-3.5" /> Push
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const StatusBadge = ({ status, payout }) => {
  if (status === "won") return <span className="iso-chip iso-chip-green"><CheckCircle2 className="size-3" /> Won · {fmtMoney(payout)}</span>;
  if (status === "lost") return <span className="iso-chip iso-chip-red"><XCircle className="size-3" /> Lost · {fmtMoney(payout)}</span>;
  if (status === "push") return <span className="iso-chip"><MinusCircle className="size-3" /> Push</span>;
  return <span className="iso-chip iso-chip-orange"><Clock className="size-3" /> Open</span>;
};

const ParlayRow = ({ bet, onSettle }) => {
  const [open, setOpen] = useState(false);
  const legs = bet.legs || [];
  return (
    <div className="iso-card p-4" data-testid={`bet-row-${bet.id}`}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex size-10 items-center justify-center rounded-xl bg-secondary-lighter text-secondary-dark">
          <Layers3 className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-gray-900">Parlay · {legs.length} legs</div>
            {bet.combined_prob != null && <span className="iso-chip-secondary iso-chip">Joint {fmtPct(bet.combined_prob, 0)}</span>}
          </div>
          <div className="mt-0.5 text-xs text-gray-500 truncate">
            {legs.map((l) => `${l.matchup.split(" @ ")[0]}/${l.market}`).slice(0, 3).join(" + ")}
            {legs.length > 3 ? ` +${legs.length - 3} more` : ""}
          </div>
        </div>
        <div className="text-right">
          <div className="iso-label !mb-0">Odds</div>
          <div className="font-lexend text-sm font-bold">{fmtOdds(bet.odds)}</div>
        </div>
        <div className="text-right">
          <div className="iso-label !mb-0">Stake</div>
          <div className="font-lexend text-sm font-bold">{fmtMoney(bet.stake)}</div>
        </div>
        <div className="text-right">
          <div className="iso-label !mb-0">To win</div>
          <div className="font-lexend text-sm font-bold text-green-dark">{fmtMoney(bet.potential_payout ?? 0)}</div>
        </div>
        <StatusBadge status={bet.status} payout={bet.payout} />
        <CLVChip clv={bet.clv} id={bet.id} />
        {bet.auto_settled && bet.status !== "open" && (
          <span className="iso-chip iso-chip-secondary !text-[10px]" title="Settled automatically from final scores" data-testid={`auto-settled-${bet.id}`}>
            <Sparkles className="size-3" /> Auto
          </span>
        )}
        <button onClick={() => setOpen((o) => !o)} className="iso-btn-ghost !p-2" data-testid={`toggle-legs-${bet.id}`}>
          <ChevronDown className={`size-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {bet.status === "open" && (
          <div className="flex flex-wrap items-center gap-1">
            <button className="iso-btn-ghost !py-1.5 !text-xs !text-green-dark" onClick={() => onSettle(bet.id, "won")} data-testid={`settle-won-${bet.id}`}>
              <CheckCircle2 className="size-3.5" /> Won
            </button>
            <button className="iso-btn-ghost !py-1.5 !text-xs !text-red-dark" onClick={() => onSettle(bet.id, "lost")} data-testid={`settle-lost-${bet.id}`}>
              <XCircle className="size-3.5" /> Lost
            </button>
          </div>
        )}
      </div>
      {open && (
        <div className="mt-3 divide-y divide-border rounded-lg border border-border bg-gray-50">
          {legs.map((leg, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5 text-xs" data-testid={`parlay-leg-detail-${bet.id}-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900">{leg.matchup}</div>
                <div className="text-gray-500">
                  {leg.market} · {leg.selection}
                  {leg.line != null ? ` (${leg.line > 0 ? "+" : ""}${leg.line})` : ""} · {leg.league}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="iso-label !mb-0">Solo prob</div>
                  <div className="font-lexend font-bold text-gray-900">{fmtPct(leg.prob, 0)}</div>
                </div>
                <div className="font-lexend text-sm font-bold text-gray-900">{fmtOdds(leg.odds)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CLVChip = ({ clv, id }) => {
  if (!clv || !clv.clv_available || clv.clv_pct == null) return null;
  const pct = clv.clv_pct;
  const pos = pct > 0.25;
  const neg = pct < -0.25;
  const Icon = pos ? TrendingUp : neg ? TrendingDown : Target;
  const klass = pos ? "iso-chip iso-chip-green" : neg ? "iso-chip iso-chip-red" : "iso-chip iso-chip-secondary";
  return (
    <Link
      to="/clv"
      className={`${klass} !text-[10px]`}
      title={`Closing Line Value — ${pos ? "You beat the close" : neg ? "Worse than close" : "Matched close"}. Click for full CLV report.`}
      data-testid={`mybets-clv-chip-${id}`}
    >
      <Icon className="size-3" /> CLV {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </Link>
  );
};

